import React, { ReactNode } from 'react';
import classnames from 'src/util/classnames';
import { ControlSkeleton, useFormLoading } from './FormLoading';

export type SegmentedOption<V> = { value: V; label: ReactNode };

export type SegmentedControlProps<V extends string | number> = {
  id: string;
  /** Accessible name of the group. */
  label: string;
  value: V;
  options: SegmentedOption<V>[];
  onChange: (value: V) => void;
  size?: 'sm' | 'xs';
};

/** A row of mutually exclusive buttons (radio group) for picking one of a few options. */
export function SegmentedControl<V extends string | number>({ id, label, value, options, onChange, size = 'sm' }: SegmentedControlProps<V>) {
  const loading = useFormLoading();
  if (loading) return <ControlSkeleton />;

  return (
    <div
      id={id}
      role="radiogroup"
      aria-label={label}
      className="inline-flex flex-wrap max-w-full rounded-md border border-solid border-gray-300 dark:border-zinc-600 overflow-hidden"
    >
      {options.map(option => {
        const selected = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`${id}-${option.value}`}
            onClick={() => onChange(option.value)}
            className={classnames(
              'border-0 border-r border-solid border-gray-300 dark:border-zinc-600 last:border-r-0 cursor-pointer whitespace-nowrap transition-colors',
              size === 'xs' ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-xs',
              selected ? 'bg-primary text-white' : 'bg-transparent theme-text-primary hover:bg-primary/10'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
